import { savePackageInclusion } from '~/components/Common/Forms/InclusionsAssetForm/utils';
import {
  type PackageInclusionRecord,
  getInclusionPersistId,
  isInclusionHidden,
} from '~/components/Offers/Detail/OfferBuilder/Content/utils';

import { normalizeBrandRegionOverrides } from '~/utils/brandRegionOverrides';
import currencyFormatter from '~/utils/currencyFormatter';
import getPackagePriceByCurrency from '~/utils/getPackagePriceByCurrency';

import type { OfferBuilderPackage, PackageStatusColor } from './types';

type PackageRecord = App.Package & Record<string, unknown>;
type PackagePriceRecord = App.Price;
type PackageInclusionSaveRecord = PackageInclusionRecord & Record<string, unknown>;
export type PackageInclusionsFieldName = 'inclusions' | 'package_bonus_inclusions' | 'bonus_inclusions';

export const REQUIRED_PACKAGE_FIELDS = [
  { key: 'name', label: 'Display name' },
  { key: 'price', label: 'Price' },
  { key: 'numberOfNights', label: 'Number of nights' },
  { key: 'roomType', label: 'Room type' },
  { key: 'ratePlan', label: 'Rate plan' },
] as const;

export type RequiredPackageField = (typeof REQUIRED_PACKAGE_FIELDS)[number];

const PACKAGE_INCLUSION_FIELD_NAMES: Array<PackageInclusionsFieldName> = [
  'inclusions',
  'package_bonus_inclusions',
  'bonus_inclusions',
];

export function getPackageName(offerPackage: App.Package): string {
  return offerPackage.name || offerPackage.deal_option_name || 'Unknown package';
}

export function getPackageDisplayNameValue(offerPackage: App.Package): string {
  return offerPackage.name || offerPackage.deal_option_name || '';
}

export function getOriginalPackageName(offerPackage: App.Package): string {
  return offerPackage.deal_option_name || offerPackage.name || 'Unknown package';
}

export function getPackagePersistId(offerPackage: App.Package) {
  const persistId = offerPackage.id_salesforce_external ?? offerPackage.le_package_id;

  return persistId ? String(persistId) : undefined;
}

export function getPackageCampaignCode(offerPackage: App.Package) {
  const campaignCode = (offerPackage as PackageRecord).campaign_code;

  return typeof campaignCode === 'string' ? campaignCode : '';
}

export function getPackageBaseCurrency(offerPackage: App.Package) {
  if (offerPackage.cost_currency) {
    return offerPackage.cost_currency;
  }

  const firstPrice = getPackagePrices(offerPackage)[0];

  if (firstPrice?.currency_code) {
    return firstPrice.currency_code;
  }

  return 'AUD';
}

function getPackagePrices(offerPackage: App.Package): Array<PackagePriceRecord> {
  return 'prices' in offerPackage && Array.isArray(offerPackage.prices) ? offerPackage.prices : [];
}

export function getPackageValue(offerPackage: App.Package) {
  const packageRecord = offerPackage as PackageRecord;
  const rawValue = Number(packageRecord.value ?? packageRecord.package_value);

  return Number.isFinite(rawValue) ? rawValue : undefined;
}

export function getPackageNumberOfNights(offerPackage: App.Package) {
  return 'number_of_nights' in offerPackage ? offerPackage.number_of_nights : undefined;
}

export function getPackageRoomTypeId(offerPackage: App.Package) {
  return 'fk_room_type_id' in offerPackage ? offerPackage.fk_room_type_id : undefined;
}

export function getPackageRoomRateId(offerPackage: App.Package) {
  return 'fk_room_rate_id' in offerPackage ? offerPackage.fk_room_rate_id : undefined;
}

export function getRoomRateLabel(roomRate: App.RoomRate) {
  return [
    roomRate.rate_plan?.name,
    roomRate.rate_plan?.rate_plan_code ? `(${roomRate.rate_plan.rate_plan_code})` : undefined,
    roomRate.rate_plan?.status === 'hidden' ? '[hidden]' : undefined,
  ]
    .filter(Boolean)
    .join(' ');
}

export function getPackageSortOrder(offerPackage: App.Package) {
  const raw = Number((offerPackage as PackageRecord).sort_order);

  return Number.isInteger(raw) && raw > 0 ? raw : undefined;
}

function getPackagePrice(offerPackage: App.Package): { currencyCode: string; price: number } | undefined {
  if (typeof offerPackage.cost_price === 'number' && Number.isFinite(offerPackage.cost_price)) {
    return {
      currencyCode: getPackageBaseCurrency(offerPackage),
      price: offerPackage.cost_price,
    };
  }

  if (getPackagePrices(offerPackage).length > 0) {
    const audPrice = getPackagePriceByCurrency(offerPackage, 'AUD') as { price?: number } | undefined;

    if (typeof audPrice?.price === 'number' && Number.isFinite(audPrice.price)) {
      return {
        currencyCode: 'AUD',
        price: audPrice.price,
      };
    }
  }

  if (typeof offerPackage.price === 'number' && Number.isFinite(offerPackage.price)) {
    return {
      currencyCode: getPackageBaseCurrency(offerPackage),
      price: offerPackage.price,
    };
  }

  return undefined;
}

export function getPackageDisplayPrice(offerPackage: App.Package) {
  return getPackagePrice(offerPackage)?.price;
}

export function getPackagePriceDisplay(offerPackage: App.Package): string {
  const price = getPackagePrice(offerPackage);

  return price !== undefined ? currencyFormatter(price.currencyCode, price.price, 0, 'code') : 'Price not available';
}

export function getPackageStayLength(offerPackage: App.Package): string {
  if ('number_of_nights' in offerPackage && offerPackage.number_of_nights) {
    return `${offerPackage.number_of_nights} nts`;
  }

  if ('number_of_days' in offerPackage && offerPackage.number_of_days) {
    return `${offerPackage.number_of_days} days`;
  }

  return 'Length not available';
}

export function getPackageSummary(offerPackage: App.Package): string {
  return `${getPackagePriceDisplay(offerPackage)} · ${getPackageStayLength(offerPackage)}`;
}

export function getPackageInclusionCount(offerPackage: App.Package): number {
  const packageRecord = offerPackage as PackageRecord;
  const inclusionFields = PACKAGE_INCLUSION_FIELD_NAMES.map((fieldName) => packageRecord[fieldName]);

  return inclusionFields.reduce<number>((count, value) => count + (Array.isArray(value) ? value.length : 0), 0);
}

export function getPackageInclusionsFieldName(offerPackage: App.Package): PackageInclusionsFieldName {
  const packageRecord = offerPackage as PackageRecord;

  return PACKAGE_INCLUSION_FIELD_NAMES.find((fieldName) => Array.isArray(packageRecord[fieldName])) ?? 'inclusions';
}

export function getPackageInclusions(offerPackage: App.Package): Array<unknown> {
  const inclusions = (offerPackage as PackageRecord)[getPackageInclusionsFieldName(offerPackage)];

  return Array.isArray(inclusions) ? inclusions : [];
}

function isPackageInclusionRecord(value: unknown): value is PackageInclusionSaveRecord {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }

  const inclusion = value as PackageInclusionSaveRecord;

  return !!(
    inclusion.id ||
    inclusion.id_salesforce_external ||
    inclusion.asset_salesforce_id ||
    inclusion.inclusion_id ||
    inclusion.inclusion?.id
  );
}

function getPersistedSortOrder(inclusion: PackageInclusionSaveRecord) {
  const sortOrder = Number(inclusion.sort_order);

  return Number.isInteger(sortOrder) && sortOrder > 0 ? sortOrder : undefined;
}

export function buildPackageSavePayload({
  offerId,
  offerPackage,
  sortOrder,
  vendorId,
}: {
  offerId?: string;
  offerPackage: App.Package;
  savedPackageInclusions?: Array<PackageInclusionRecord>;
  sortOrder?: number;
  vendorId?: string;
}) {
  const packageId = getPackagePersistId(offerPackage);
  const packageData = JSON.parse(JSON.stringify(offerPackage)) as Record<string, unknown>;
  const packageName = getPackageName(offerPackage);
  const inclusions = getPackageInclusions(offerPackage).filter(isPackageInclusionRecord);
  const hiddenSortOrders = new Set(
    inclusions
      .filter(isInclusionHidden)
      .map(getPersistedSortOrder)
      .filter((sortOrder): sortOrder is number => sortOrder !== undefined),
  );
  let nextVisibleSortOrder = 1;
  const packageInclusions = inclusions.flatMap((inclusion) => {
    const inclusionId = getInclusionPersistId(inclusion);
    const offerIdValue = String(inclusion.offer_id ?? offerId ?? '');
    const packageIdValue = String(inclusion.package_id ?? packageId ?? '');

    if (!inclusionId || !offerIdValue || !packageIdValue) {
      return [];
    }

    let inclusionSortOrder = isInclusionHidden(inclusion) ? getPersistedSortOrder(inclusion) : undefined;

    if (inclusionSortOrder === undefined) {
      while (hiddenSortOrders.has(nextVisibleSortOrder)) {
        nextVisibleSortOrder += 1;
      }

      inclusionSortOrder = nextVisibleSortOrder;
      nextVisibleSortOrder += 1;
    }

    return [
      savePackageInclusion(
        {
          ...inclusion,
          id: inclusion.id,
          inclusion_id: inclusionId,
          offer_id: offerIdValue,
          package_id: packageIdValue,
          package_name: inclusion.package_name ?? packageName,
          name: inclusion.name ?? inclusion.inclusion?.name ?? inclusion.content,
          sort_order: inclusionSortOrder,
        },
        vendorId,
      ),
    ];
  });

  PACKAGE_INCLUSION_FIELD_NAMES.forEach((fieldName) => {
    delete packageData[fieldName];
  });

  const existingSortOrder = Number(packageData.sort_order);
  packageData.sort_order = sortOrder ?? (Number.isInteger(existingSortOrder) ? existingSortOrder : 0);
  packageData.campaign_code = typeof packageData.campaign_code === 'string' ? packageData.campaign_code : '';
  packageData.inclusion_highlights =
    typeof packageData.inclusion_highlights === 'string' ? packageData.inclusion_highlights : '';

  if (Array.isArray(packageData.package_target_upgrades)) {
    packageData.package_target_upgrades = packageData.package_target_upgrades.filter(
      (upgrade) =>
        typeof upgrade === 'object' &&
        upgrade !== null &&
        'target_package_id' in upgrade &&
        (upgrade as Record<string, unknown>).target_package_id,
    );
  }

  if ('fk_room_rate_id' in packageData && !packageData.fk_room_rate_id) {
    delete packageData.fk_room_rate_id;
  }

  if ('fk_room_type_id' in packageData && !packageData.fk_room_type_id) {
    delete packageData.fk_room_type_id;
  }

  packageData.brand_region_overrides = normalizeBrandRegionOverrides(
    packageData.brand_region_overrides as Parameters<typeof normalizeBrandRegionOverrides>[0],
  );

  return {
    offerId,
    packageData,
    packageId,
    packageInclusions,
  };
}

export function getPackageStatus(offerPackage: App.Package): { color: PackageStatusColor; label: string } {
  switch (offerPackage.status) {
    case 'content-approved':
      return { color: 'success', label: 'Approved' };
    case 'draft':
      return { color: 'warning', label: 'Draft' };
    case 'hidden':
      return { color: 'default', label: 'Hidden' };
    default:
      return { color: 'default', label: offerPackage.status || 'Unknown' };
  }
}

export function getMissingRequiredPackageFields(offerPackage: App.Package): Array<RequiredPackageField> {
  const packageName = getPackageDisplayNameValue(offerPackage);

  return REQUIRED_PACKAGE_FIELDS.filter((field) => {
    switch (field.key) {
      case 'name':
        return !packageName.trim();
      case 'price':
        return !isPositiveNumber(getPackageDisplayPrice(offerPackage));
      case 'numberOfNights':
        return !isPositiveNumber(getPackageNumberOfNights(offerPackage));
      case 'roomType':
        return !getPackageRoomTypeId(offerPackage);
      case 'ratePlan':
        return !getPackageRoomRateId(offerPackage);
      default:
        return false;
    }
  });
}

function isPositiveNumber(value?: number) {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

export function buildOfferBuilderPackages(packages: Array<App.Package>) {
  return packages
    .map((offerPackage, index): OfferBuilderPackage => {
      const persistId = getPackagePersistId(offerPackage);
      const status = getPackageStatus(offerPackage);

      return {
        name: getPackageName(offerPackage),
        originalPackageName: getOriginalPackageName(offerPackage),
        package: offerPackage,
        packageSummary: getPackageSummary(offerPackage),
        sortableId: persistId ? `package:${persistId}:${index}` : `package-index:${index}`,
        sortOrder: getPackageSortOrder(offerPackage),
        statusColor: status.color,
        statusLabel: status.label,
      };
    })
    .sort((a, b) => {
      if (a.sortOrder !== undefined && b.sortOrder !== undefined && a.sortOrder !== b.sortOrder) {
        return a.sortOrder - b.sortOrder;
      }

      if (a.sortOrder !== undefined && b.sortOrder === undefined) {
        return -1;
      }

      if (a.sortOrder === undefined && b.sortOrder !== undefined) {
        return 1;
      }

      const aPrice = getPackageDisplayPrice(a.package) ?? Number.POSITIVE_INFINITY;
      const bPrice = getPackageDisplayPrice(b.package) ?? Number.POSITIVE_INFINITY;

      if (aPrice !== bPrice) {
        return aPrice - bPrice;
      }

      return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
    });
}
